import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Typography, Breadcrumb, Card, Row, Col, Button, Tabs, Switch, Space, Divider, Input, Select, Form, message, Alert, Segmented, List, Avatar, Tag, Modal, Upload, Tooltip } from 'antd';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';
import { 
  FileTextOutlined, 
  EnvironmentOutlined, 
  VideoCameraOutlined,
  SaveOutlined,
  SyncOutlined,
  SettingOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  FileOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';

const { Title, Paragraph } = Typography;

const AddinsConfiguration = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('insert-key');
  const [insertKeyForm] = Form.useForm();
  const [mapReplayForm] = Form.useForm();
  const [cameraForm] = Form.useForm();
  
  // Geotab integration state
  const [geotabDatabases, setGeotabDatabases] = useState([]);
  const [selectedDatabase, setSelectedDatabase] = useState(null);
  const [registering, setRegistering] = useState(false);

  // AddinsConfigration state
  const [allowUnverified, setAllowUnverified] = useState(true);
  const [addins, setAddins] = useState([
    { key: 'collision', name: 'Collision Reconstruction', status: 'verified' },
    { key: 'data-connector', name: 'Data Connector Demo', status: 'verified' },
    { key: 'drive-fuel-tracker', name: 'Drive Fuel Tracker (by Geotab)', status: 'verified' },
    { key: 'eld-info', name: 'ELD Info', status: 'verified' },
    { key: 'eld-settings', name: 'EldSettingsValidator', status: 'verified' },
    { key: 'evsa', name: 'EVSA', status: 'verified' },
    { key: 'iox-output', name: 'IOX Output', status: 'unverified' },
    { key: 'last-insert-key-report', name: 'Last Insert Key Report', status: 'unverified' },
    { key: 'map-replay', name: 'MapReplay', status: 'unverified' },
    { key: 'camera-addin', name: 'mygeotab-camera-addin', status: 'verified' },
    { key: 'roadside', name: 'Roadside Assistance', status: 'verified' },
    { key: 'tabs', name: 'Tabs', status: 'unverified' },
  ]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [activeModalTab, setActiveModalTab] = useState('config');
  const [configJson, setConfigJson] = useState(
    '{\n  "name": "",\n  "supportEmail": "ramisj12@gmail.com",\n  "version": "1.0",\n  "items": [\n    {\n      "url": "",\n      "path": "",\n      "menuName": {\n        "en": ""\n      }\n    }\n  ]\n}'
  );

  const handleInsertKeySave = async (values) => {
    try {
      console.log('Insert Key values:', values);
      message.success('Insert Key Report settings saved successfully');
    } catch (error) {
      message.error('Failed to save settings');
    }
  };

  const handleMapReplaySave = async (values) => {
    try {
      console.log('Map Replay values:', values);
      message.success('Map Replay settings saved successfully');
    } catch (error) {
      message.error('Failed to save settings');
    }
  };

  const handleCameraSave = async (values) => {
    try {
      console.log('Camera values:', values);
      message.success('Surveillance Camera settings saved successfully');
    } catch (error) {
      message.error('Failed to save settings');
    }
  };

  const authHeader = useCallback(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  // Fetch Geotab databases on component mount
  useEffect(() => {
    const fetchGeotabDatabases = async () => {
      try {
        const { data } = await axios.get('/api/integrations/geotab/accounts', { headers: authHeader() });
        const items = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
        setGeotabDatabases(items);
        if (items.length > 0) {
          setSelectedDatabase(items[0].database);
        }
      } catch (error) {
        console.error('Failed to fetch Geotab databases:', error);
      }
    };
    fetchGeotabDatabases();
  }, [authHeader]);

  const handleRegisterAddin = async () => {
    if (!selectedDatabase) {
      message.error('Please select a Geotab database');
      return;
    }

    try {
      setRegistering(true);
      
      await axios.post('/api/geotab-addins/register', {
        geotabDatabase: selectedDatabase,
        addinConfig: configJson
      }, { headers: authHeader() });

      message.success('Add-in registered successfully to your Geotab account!');
      setModalOpen(false);
    } catch (error) {
      console.error('Failed to register add-in:', error);
      message.error(error?.response?.data?.error || 'Failed to register add-in');
    } finally {
      setRegistering(false);
    }
  };

  // AddinsConfigration helper functions and data
  const manifestByKey = useMemo(() => ({
    collision: 'https://app.geotab.com/addins/geotab/accidentreconstruction/manifest.json',
    'data-connector': 'https://app.geotab.com/addins/geotab/geotab-data-connector-demo/manifest.json',
    'drive-fuel-tracker': 'https://app.geotab.com/addins/geotab/drive-fuel-tracker/manifest.json',
    'eld-info': 'https://app.geotab.com/addins/geotab/eld-support/manifest.json',
    'eld-settings': 'https://app.geotab.com/addins/geotab/eld-settings-validator/manifest.json',
    evsa: 'https://app.geotab.com/addins/geotab/evsa/manifest.json',
    'map-replay': 'https://app.geotab.com/addins/mygeotabcameraaddin/production/manifest.json',
    'camera-addin': 'https://app.geotab.com/addins/mygeotabcameraaddin/production/manifest.json',
    roadside: 'https://app.geotab.com/addins/geotab/roadside-assistance/manifest.json',
  }), []);

  const presetConfigByKey = useMemo(() => ({
    tabs: `{
  "name": "Tabs",
  "supportEmail": "justin@fleet-nomics.com",
  "version": "1.0",
  "items": [
    {
      "url": "signInButton.html",
      "path": "SurveillanceCameras",
      "menuName": {
        "en": "Surveillance Cameras"
      },
      "svgIcon": "https://geotab-images.s3.us-east-2.amazonaws.com/icon.svg"
    }
  ],
  "isSigned": false
}`,
    'iox-output': `{
  "name": "IOX Output",
  "supportEmail": "support@geotab.com",
  "version": "1.1.0",
  "items": [
    {
      "url": "https://cdn.jsdelivr.net/gh/Geotab/sdk-addin-samples@master/addin-iox-output/dist/ioxOutput.html",
      "path": "AdministrationLink/",
      "menuName": {
        "en": "IOX Output"
      },
      "icon": "https://cdn.jsdelivr.net/gh/Geotab/sdk-addin-samples@master/addin-iox-output/dist/images/icon.svg"
    }
  ],
  "isSigned": false
}`,
    'last-insert-key-report': `{
  "name": "Last Insert Key Report",
  "supportEmail": "dmitryradyno@geotab.com",
  "version": "1.0",
  "items": [
    {
      "url": "https://www.geotab.com/mygeotab/addin/lastkeyinsert/lastinsertkey.html",
      "path": "ActivityLink/",
      "menuName": {
        "en": "Last Insert Key Report"
      }
    }
  ],
  "isSigned": false
}`,
  }), []);

  const openAddModal = () => {
    setEditing(null);
    setActiveModalTab('config');
    setConfigJson(
      '{\n  "name": "",\n  "supportEmail": "ramisj12@gmail.com",\n  "version": "1.0",\n  "items": [\n    {\n      "url": "",\n      "path": "",\n      "menuName": {\n        "en": ""\n      }\n    }\n  ]\n}'
    );
    setModalOpen(true);
  };

  const openEditModal = (item) => {
    setEditing(item);
    setActiveModalTab('config');
    const preset = presetConfigByKey[item?.key];
    if (preset) {
      setConfigJson(preset);
    } else {
      const url = manifestByKey[item?.key] || '';
      setConfigJson(url ? `{
  "url": "${url}"
}` : `{
  "url": ""
}`);
    }
    setModalOpen(true);
  };

  const handleDelete = (key) => {
    setAddins(prev => prev.filter(a => a.key !== key));
  };

  const statusTag = (status) => (
    <Tag color={status === 'verified' ? 'green' : 'orange'} style={{ marginInlineStart: 8 }}>
      {status === 'verified' ? 'Verified' : 'Unverified'}
    </Tag>
  );

  const dataToRender = useMemo(() => addins, [addins]);

  const tabItems = [
    {
      key: 'insert-key',
      label: (
        <span>
          <FileTextOutlined /> Latest Insert Key Report
        </span>
      ),
      children: (
        <div>
          <Card>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <Title level={5}>Insert Key Report Configuration</Title>
                <Paragraph type="secondary">
                  Configure settings for the Latest Insert Key Report add-in. This tool tracks and reports vehicle ignition events.
                </Paragraph>
              </div>

              <Form
                form={insertKeyForm}
                layout="vertical"
                onFinish={handleInsertKeySave}
                initialValues={{
                  enabled: true,
                  reportInterval: 'daily',
                  emailNotifications: true,
                  includeLocation: true,
                  includeTimestamp: true,
                  maxRecords: 100,
                }}
              >
                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item label="Enable Insert Key Report" name="enabled" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                  </Col>
                </Row>

                <Divider />

                <Title level={5}>Report Settings</Title>
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item label="Report Interval" name="reportInterval">
                      <Select size="large">
                        <Select.Option value="realtime">Real-time</Select.Option>
                        <Select.Option value="hourly">Hourly</Select.Option>
                        <Select.Option value="daily">Daily</Select.Option>
                        <Select.Option value="weekly">Weekly</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item label="Maximum Records" name="maxRecords">
                      <Input size="large" type="number" placeholder="100" />
                    </Form.Item>
                  </Col>
                </Row>

                <Divider />

                <Title level={5}>Data Options</Title>
                <Form.Item label="Include Location Data" name="includeLocation" valuePropName="checked">
                  <Switch />
                </Form.Item>
                <Form.Item label="Include Timestamp" name="includeTimestamp" valuePropName="checked">
                  <Switch />
                </Form.Item>
                <Form.Item label="Email Notifications" name="emailNotifications" valuePropName="checked">
                  <Switch />
                </Form.Item>

                <Divider />

                <Form.Item>
                  <Space>
                    <Button type="primary" htmlType="submit" size="large" icon={<SaveOutlined />}>
                      Save Settings
                    </Button>
                    <Button size="large" icon={<SyncOutlined />}>
                      Generate Report Now
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </Space>
          </Card>
        </div>
      )
    },
    {
      key: 'map-replay',
      label: (
        <span>
          <EnvironmentOutlined /> Map Replay
        </span>
      ),
      children: (
        <div>
          <Card>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <Title level={5}>Map Replay Configuration</Title>
                <Paragraph type="secondary">
                  Configure settings for the Map Replay add-in. Visualize and replay vehicle routes on an interactive map.
                </Paragraph>
              </div>

              <Form
                form={mapReplayForm}
                layout="vertical"
                onFinish={handleMapReplaySave}
                initialValues={{
                  enabled: true,
                  mapProvider: 'google',
                  playbackSpeed: 'normal',
                  showTrail: true,
                  showMarkers: true,
                  autoRefresh: false,
                  refreshInterval: 60,
                }}
              >
                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item label="Enable Map Replay" name="enabled" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                  </Col>
                </Row>

                <Divider />

                <Title level={5}>Map Settings</Title>
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item label="Map Provider" name="mapProvider">
                      <Select size="large">
                        <Select.Option value="google">Google Maps</Select.Option>
                        <Select.Option value="mapbox">Mapbox</Select.Option>
                        <Select.Option value="openstreet">OpenStreetMap</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item label="Playback Speed" name="playbackSpeed">
                      <Select size="large">
                        <Select.Option value="slow">Slow (0.5x)</Select.Option>
                        <Select.Option value="normal">Normal (1x)</Select.Option>
                        <Select.Option value="fast">Fast (2x)</Select.Option>
                        <Select.Option value="veryfast">Very Fast (4x)</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Divider />

                <Title level={5}>Display Options</Title>
                <Form.Item label="Show Route Trail" name="showTrail" valuePropName="checked">
                  <Switch />
                </Form.Item>
                <Form.Item label="Show Location Markers" name="showMarkers" valuePropName="checked">
                  <Switch />
                </Form.Item>
                <Form.Item label="Auto-Refresh Data" name="autoRefresh" valuePropName="checked">
                  <Switch />
                </Form.Item>

                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item label="Refresh Interval (seconds)" name="refreshInterval">
                      <Input size="large" type="number" placeholder="60" />
                    </Form.Item>
                  </Col>
                </Row>

                <Divider />

                <Form.Item>
                  <Space>
                    <Button type="primary" htmlType="submit" size="large" icon={<SaveOutlined />}>
                      Save Settings
                    </Button>
                    <Button size="large" icon={<EnvironmentOutlined />}>
                      Open Map Replay
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </Space>
          </Card>
        </div>
      )
    },
    {
      key: 'cameras',
      label: (
        <span>
          <VideoCameraOutlined /> Surveillance Cameras
        </span>
      ),
      children: (
        <div>
          <Card>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <Title level={5}>Surveillance Camera Configuration</Title>
                <Paragraph type="secondary">
                  Configure settings for surveillance camera integration. Monitor and record video feeds from connected cameras.
                </Paragraph>
              </div>

              <Form
                form={cameraForm}
                layout="vertical"
                onFinish={handleCameraSave}
                initialValues={{
                  enabled: false,
                  cameraProvider: 'generic',
                  recordingQuality: 'high',
                  storageLocation: 'cloud',
                  motionDetection: true,
                  alertsEnabled: true,
                  retentionDays: 30,
                }}
              >
                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item label="Enable Surveillance Cameras" name="enabled" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                  </Col>
                </Row>

                <Divider />

                <Title level={5}>Camera Settings</Title>
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item label="Camera Provider" name="cameraProvider">
                      <Select size="large">
                        <Select.Option value="generic">Generic IP Camera</Select.Option>
                        <Select.Option value="hikvision">Hikvision</Select.Option>
                        <Select.Option value="dahua">Dahua</Select.Option>
                        <Select.Option value="axis">Axis</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item label="Recording Quality" name="recordingQuality">
                      <Select size="large">
                        <Select.Option value="low">Low (480p)</Select.Option>
                        <Select.Option value="medium">Medium (720p)</Select.Option>
                        <Select.Option value="high">High (1080p)</Select.Option>
                        <Select.Option value="ultra">Ultra (4K)</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item label="Storage Location" name="storageLocation">
                      <Select size="large">
                        <Select.Option value="cloud">Cloud Storage</Select.Option>
                        <Select.Option value="local">Local Storage</Select.Option>
                        <Select.Option value="hybrid">Hybrid</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item label="Retention Period (days)" name="retentionDays">
                      <Input size="large" type="number" placeholder="30" />
                    </Form.Item>
                  </Col>
                </Row>

                <Divider />

                <Title level={5}>Detection & Alerts</Title>
                <Form.Item label="Motion Detection" name="motionDetection" valuePropName="checked">
                  <Switch />
                </Form.Item>
                <Form.Item label="Alert Notifications" name="alertsEnabled" valuePropName="checked">
                  <Switch />
                </Form.Item>

                <Divider />

                <Form.Item>
                  <Space>
                    <Button type="primary" htmlType="submit" size="large" icon={<SaveOutlined />}>
                      Save Settings
                    </Button>
                    <Button size="large" icon={<VideoCameraOutlined />}>
                      View Camera Feeds
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </Space>
          </Card>
        </div>
      )
    },
    {
      key: 'addins-management',
      label: (
        <span>
          <SettingOutlined /> Add-Ins Configuration
        </span>
      ),
      children: (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h2 style={{ margin: 0 }}>Add-Ins Management</h2>
            <Button type="primary" icon={<PlusOutlined />} onClick={openAddModal}>Add-In</Button>
          </div>

          <Card bodyStyle={{ paddingTop: 12 }}>
            <Alert
              type="warning"
              showIcon
              icon={<ExclamationCircleOutlined />}
              message="Unverified Add-Ins could have potential security risks. We recommend performing a security audit before allowing them."
              style={{ marginBottom: 12 }}
            />

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <div style={{ minWidth: 180 }}>Allow unverified Add-Ins</div>
              <Segmented
                options={[{ label: 'Yes', value: true }, { label: 'No', value: false }]}
                value={allowUnverified}
                onChange={(v) => setAllowUnverified(Boolean(v))}
              />
            </div>

            <List
              itemLayout="horizontal"
              dataSource={dataToRender}
              renderItem={(item) => (
                <List.Item
                  actions={[
                    statusTag(item.status),
                    <Tooltip key="edit" title="Edit">
                      <Button type="text" icon={<EditOutlined />} onClick={() => openEditModal(item)} />
                    </Tooltip>,
                    <Tooltip key="delete" title="Delete">
                      <Button danger type="text" icon={<DeleteOutlined />} onClick={() => handleDelete(item.key)} />
                    </Tooltip>
                  ]}
                  style={{ paddingLeft: 8, paddingRight: 8 }}
                >
                  <List.Item.Meta
                    avatar={<Avatar size={24} icon={<FileOutlined />} />}
                    title={
                      <Space>
                        <span style={{ fontWeight: 500 }}>{item.name}</span>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>

          <Modal
            title={editing ? `Add-In - ${editing.name}` : 'Add-In'}
            open={modalOpen}
            onCancel={() => setModalOpen(false)}
            footer={null}
            width={720}
            destroyOnClose
          >
            <Tabs
              activeKey={activeModalTab}
              onChange={setActiveModalTab}
              items={[
                {
                  key: 'config',
                  label: 'Configuration',
                  children: (
                    <div>
                      <div style={{ marginBottom: 16 }}>
                        <Alert
                          message="Register to Geotab"
                          description="Configure your add-in below and register it to your Geotab account. It will appear in your MyGeotab Add-Ins menu."
                          type="info"
                          showIcon
                          style={{ marginBottom: 16 }}
                        />
                        
                        <div style={{ marginBottom: 8 }}>
                          <label style={{ fontWeight: 500 }}>Select Geotab Database</label>
                        </div>
                        <Select
                          style={{ width: '100%', marginBottom: 16 }}
                          value={selectedDatabase}
                          onChange={setSelectedDatabase}
                          placeholder="Select a Geotab database"
                        >
                          {geotabDatabases.map(db => (
                            <Select.Option key={db.database} value={db.database}>
                              {db.name || db.database}
                            </Select.Option>
                          ))}
                        </Select>
                      </div>
                      
                      <div style={{ marginBottom: 8 }}>Configuration JSON</div>
                      <Input.TextArea
                        rows={12}
                        value={configJson}
                        onChange={(e) => setConfigJson(e.target.value)}
                        style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}
                      />
                      <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between' }}>
                        <Button onClick={() => setModalOpen(false)}>Cancel</Button>
                        <Space>
                          <Button onClick={() => setModalOpen(false)}>Save Locally</Button>
                          <Button 
                            type="primary" 
                            onClick={handleRegisterAddin}
                            loading={registering}
                            disabled={!selectedDatabase}
                          >
                            Register to Geotab
                          </Button>
                        </Space>
                      </div>
                    </div>
                  )
                },
                {
                  key: 'files',
                  label: 'Files',
                  children: (
                    <div>
                      <Upload.Dragger name="file" multiple={false} showUploadList>
                        <p className="ant-upload-drag-icon">
                          <FileOutlined />
                        </p>
                        <p className="ant-upload-text">Drop file here or click to browse</p>
                        <p className="ant-upload-hint">Drag your ZIP-archived Add-In or Add-In configuration file.</p>
                      </Upload.Dragger>
                      <div style={{ marginTop: 12, textAlign: 'right' }}>
                        <Space>
                          <Button onClick={() => setModalOpen(false)}>Cancel</Button>
                          <Button type="primary" onClick={() => setModalOpen(false)}>Done</Button>
                        </Space>
                      </div>
                    </div>
                  )
                }
              ]}
            />
          </Modal>
        </div>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={[{ title: 'Client' }, { title: 'Add-Ins Configuration' }]} />
      
      <div style={{ marginTop: 12, marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>Add-Ins Configuration</Title>
        <Paragraph type="secondary" style={{ marginTop: 8 }}>
          Configure and manage your add-in integrations and features
        </Paragraph>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        size="large"
        type="card"
      />
    </div>
  );
};

export default AddinsConfiguration;
